import { TimesOutlineIcon } from '@ikyucom/design-system-icons-react'
import { forwardRef, useCallback, useRef } from 'react'
import type { ComponentProps, MouseEvent, PropsWithChildren } from 'react'
import { useSafeBack } from '~/navigation'
import { useIsLanding } from '~/navigation/atoms'
import { type RecipeVariantProps, cx, sva } from '~/styled-system/css'
import { FusumaTitle } from './Fusuma'
import { useOpenCloseAnimation } from './Provider'
import type { StackableSlideUpModal } from './StackableSlideUpModal'

type Props = Pick<ComponentProps<typeof StackableSlideUpModal>, 'willBeUnmounted'> &
  Omit<ComponentProps<typeof Fusuma>, 'opened' | 'close'>

const openKeyframes = [
  { transform: 'translateX(100%)' },
  { transform: 'translateX(0)' },
] as const satisfies Keyframe[]

export function StackableFusuma({ willBeUnmounted, children, ...props }: PropsWithChildren<Props>) {
  const back = useSafeBack()
  const ref = useRef<HTMLDivElement>(null)
  useOpenCloseAnimation({
    ref,
    willBeUnmounted,
    openKeyframes,
    removeTransformAfterAnimation: true,
  })
  return (
    <Fusuma {...props} opened={true} close={back} ref={ref}>
      {children}
    </Fusuma>
  )
}

type FusumaProps = {
  title?: string
  close?: () => void
  opened: boolean
  layout?: 'full'
  bodyClassName?: string
}

const fusumaStyles = sva({
  slots: ['overlay', 'container', 'close', 'closeIcon', 'section', 'modal'],
  base: {
    overlay: {
      position: 'fixed',
      inset: '0',
      _pc: {
        transitionBehavior: 'ease-out',
        transitionProperty: 'background-color',
        transitionDuration: '[200ms]',
        backgroundColor: '[transparent]',
      },
    },
    container: {
      position: 'absolute',
      right: '0',
      height: 'screen.height.full',
      width: 'screen.width.full',
      bgColor: 'white',
      _pc: {
        // PC のときふすまの幅は固定
        // figma design より, 本体が 786px で padding 32px (8) なので 850px に設定
        // https://www.figma.com/design/Xizn2jb2WlVtzaAoZf0maY/Master?node-id=12242-128840&m=dev
        width: '[850px]',
      },
    },
    close: {
      display: 'none',
      _pc: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'absolute',
        w: '[56px]',
        h: '[56px]',
        top: '[0px]',
        left: '[-56px]',
        transitionBehavior: 'ease-out',
        transitionProperty: 'opacity',
        transitionDuration: '[200ms]',
        opacity: '[0]',
      },
    },
    // close ボタンが PC でしか表示されないので responsive 不要
    closeIcon: {
      h: '[48px]',
      w: '[48px]',
      color: 'white',
    },
    // NOTE: この .relative に対応する absolute が PaymentAmountDetails に存在
    section: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      height: 'full',
      width: 'full',
      _pc: {
        p: '8',
        gap: '8',
      },
    },
    modal: {
      height: 'full',
      overflowY: 'scroll',
      backgroundColor: 'white',
      // PC の場合は chrome 中心になるはずなので scrollbar-gutter が使える
      _pc: {
        overflowY: 'auto',
        scrollbarGutter: '[stable]',
      },
    },
  },
  variants: {
    landing: {
      true: {
        overlay: {
          _pc: {
            transitionProperty: 'none',
          },
        },
        container: {
          _pc: {
            transform: 'none',
          },
        },
      },
    },
    state: {
      open: {
        overlay: {
          _pc: {
            backgroundColor: '[rgb(0, 0, 0, 0.35)]',
          },
        },
        close: {
          _pc: {
            opacity: '[1]',
          },
        },
      },
      closed: {
        overlay: {
          _pc: {
            transitionProperty: 'background-color',
          },
        },
      },
    },
    headerColor: {
      neutral: {},
      white: {
        container: {
          backgroundColor: 'gray.100',
        },
      },
    },
    layout: {
      full: {
        section: {
          _pc: {
            p: '0',
          },
        },
      },
    },
  },
})

const Fusuma = forwardRef<
  HTMLDivElement,
  PropsWithChildren<FusumaProps & RecipeVariantProps<typeof fusumaStyles>>
>(function Fusuma(
  { title, opened, close, children, headerColor = 'neutral', layout, bodyClassName },
  ref
) {
  const overlay = useRef<HTMLDivElement>(null)
  const onOverlayClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      // イベントの発生源が overlay のときだけ閉じる
      if (close !== undefined && e.currentTarget === e.target && e.target === overlay.current) {
        close()
      }
    },
    [close]
  )

  // ランディング時に mount されていれば、アニメーションさせない
  // soft navigation で isLanding が false になっても変わらない
  const landing = useRef(useIsLanding())
  const classes = fusumaStyles({
    state: opened ? 'open' : 'closed',
    layout,
    landing: landing.current,
  })
  return (
    <div ref={overlay} className={classes.overlay} onClick={onOverlayClick}>
      <div
        ref={ref}
        className={classes.container}
        // 初回ランディング時はアニメーションなしでふすまを開くので閉じた状態としない
        // アニメーション完了時の状態を維持するため commitStyles で style に反映しているので、初期状態は style に書く
        style={landing.current ? {} : { transform: 'translateX(100%)' }}
      >
        <button type="button" className={classes.close} onClick={close} aria-label="閉じる">
          <TimesOutlineIcon className={classes.closeIcon} />
        </button>
        <section className={classes.section}>
          {title && (
            <FusumaTitle title={title} close={close} headerColor={headerColor} layout={layout} />
          )}
          {opened && (
            <div data-fusuma className={cx(classes.modal, bodyClassName)}>
              {children}
            </div>
          )}
        </section>
      </div>
    </div>
  )
})
